import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  ArrowLeft,
  Check,
  FolderPlus,
  Upload,
  Filter,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from "lucide-react";
import { Button, Input } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { FolderTree, type FolderTreeAction } from "@/components/dms/FolderTree";
import { FolderContents } from "@/components/dms/FolderContents";
import { DocumentUploadModal } from "@/components/dms/DocumentUploadModal";
import { FolderPermissionsModal } from "@/components/dms/FolderPermissionsModal";
import {
  DocumentFilterPanel,
  countActiveFilters,
  type DocumentFilters,
} from "@/components/dms/DocumentFilterPanel";
import { foldersApi } from "@/api/folders";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { EntityRootBrowser, ROOT_BROWSER_PAGE_SIZE } from "./EntityRootBrowser";
import type {
  DocumentCategory,
  DocumentFolderDto,
  DocumentFolderTreeDto,
  FolderSearchSortBy,
  IngestionStatus,
} from "@/types/api";

type SortOption = {
  value: FolderSearchSortBy;
  dir: "asc" | "desc";
  label: string;
};

const SORT_OPTIONS: SortOption[] = [
  { value: "name", dir: "asc", label: "Name (A→Z)" },
  { value: "name", dir: "desc", label: "Name (Z→A)" },
  { value: "created", dir: "desc", label: "Newest first" },
  { value: "created", dir: "asc", label: "Oldest first" },
  { value: "recent", dir: "desc", label: "Recently used" },
];

const FILTER_PARAM_KEYS = [
  "category",
  "ingestionStatus",
  "uploadedBy",
  "dateFrom",
  "dateTo",
  "tags",
] as const;

/**
 * DMS v1.0 §5a — Document Explorer at /documents. Left tree, main
 * folder contents, top toolbar with search + filters + upload.
 */
export function DocumentsPage() {
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canUpload = hasPermission("Documents.Create");
  const canCreateFolder = hasPermission("Documents.CreateFolder");
  const canViewArchived = hasPermission("Documents.ViewArchived");
  const [searchParams, setSearchParams] = useSearchParams();
  // Document Archive v1.0 — URL-persisted toggle. ?showArchived=true flips
  // the folder list to "All" scope (active + archived together). The toggle
  // is suppressed for users without Documents.ViewArchived.
  const showArchived = searchParams.get("showArchived") === "true";
  const setShowArchived = (next: boolean) => {
    setSearchParams(
      (prev) => {
        if (next) prev.set("showArchived", "true");
        else prev.delete("showArchived");
        return prev;
      },
      { replace: true },
    );
  };

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterAnchorRef = useRef<HTMLButtonElement>(null);
  // Layout redesign — the folder tree lives in a collapsible 280px left
  // column; collapsed it shrinks to a 56px icon-only strip.
  const [treeCollapsed, setTreeCollapsed] = useState(false);

  // Layout redesign — the category pills, folder search, and sort control
  // moved into the top bar. They drive the server-side root-folder search
  // whose results render in the left column when no root is pinned.
  const [browseQuery, setBrowseQuery] = useState("");
  const [browseSortKey, setBrowseSortKey] = useState("name|asc");
  const [browsePage, setBrowsePage] = useState(1);

  // Prompt 6 — DMS browser at scale. Replaces the legacy load-every-root-
  // tree pane. selectedCategory drives the entity-root list; selectedRoot
  // pins one entity's tree into the sidebar so the user can drill in.
  // Both are URL-persisted so deep links and back/forward survive.
  const selectedCategory = searchParams.get("category-browser") || null;
  const selectedRootId = searchParams.get("rootId") || null;
  const setSelectedCategory = (next: string | null) => {
    setSearchParams(
      (prev) => {
        if (next) prev.set("category-browser", next);
        else prev.delete("category-browser");
        // Switching category clears any pinned root/folder so the right
        // pane doesn't show stale content from a different tree.
        prev.delete("rootId");
        return prev;
      },
      { replace: true },
    );
    setSelectedFolderId(null);
    setBrowsePage(1);
  };
  const setSelectedRoot = (root: DocumentFolderDto | null) => {
    setSearchParams(
      (prev) => {
        if (root) prev.set("rootId", root.id);
        else prev.delete("rootId");
        return prev;
      },
      { replace: true },
    );
    setSelectedFolderId(root?.id ?? null);
  };

  // Document filters — URL-persisted alongside showArchived. Reading them
  // from `searchParams` directly (as opposed to local state) means a
  // reload, deep link, or back/forward nav restores the filter UI to the
  // exact same state it was applied in.
  const filters: DocumentFilters = useMemo(() => ({
    category: (searchParams.get("category") || undefined) as DocumentCategory | undefined,
    ingestionStatus: (searchParams.get("ingestionStatus") || undefined) as IngestionStatus | undefined,
    uploadedBy: searchParams.get("uploadedBy") || undefined,
    dateFrom: searchParams.get("dateFrom") || undefined,
    dateTo: searchParams.get("dateTo") || undefined,
    tags: searchParams.get("tags") || undefined,
  }), [searchParams]);
  const activeFilterCount = countActiveFilters(filters);

  const applyFilters = (next: DocumentFilters) => {
    setSearchParams(
      (prev) => {
        for (const key of FILTER_PARAM_KEYS) {
          const value = next[key as keyof DocumentFilters];
          if (value && value !== "") prev.set(key, String(value));
          else prev.delete(key);
        }
        return prev;
      },
      { replace: true },
    );
    setFilterOpen(false);
  };

  // Clear filter params only — preserves showArchived and any future
  // unrelated query state. The spec is explicit about this.
  const clearFilters = () => {
    setSearchParams(
      (prev) => {
        for (const key of FILTER_PARAM_KEYS) prev.delete(key);
        return prev;
      },
      { replace: true },
    );
    setFilterOpen(false);
  };

  // Prompt 6 — single-root tree for the pinned entity. The legacy code
  // loaded every root tree up front; at thousands of entity roots that
  // is unworkable. Now we only fetch the one tree the user picked from
  // the browser. The query is split on showArchived so toggling the
  // switch re-fetches without re-mounting.
  const tree = useQuery({
    queryKey: [
      "dms",
      "rootTree",
      selectedRootId,
      showArchived ? "incl-archived" : "active",
    ],
    enabled: !!selectedRootId,
    queryFn: () => foldersApi.tree(selectedRootId!, showArchived),
  });
  const trees = useMemo(
    () => (tree.data ? [tree.data] : []),
    [tree.data],
  );

  // Layout redesign — server-side root-folder search powering the top-bar
  // category pills (counts) + the browse list in the left column. Only
  // runs while no root is pinned, since the pinned state shows the tree
  // instead. Page resets to 1 whenever category / q / sort change.
  const [browseSortBy, browseSortDir] = useMemo<[FolderSearchSortBy, "asc" | "desc"]>(() => {
    const [s, d] = browseSortKey.split("|");
    return [s as FolderSearchSortBy, d as "asc" | "desc"];
  }, [browseSortKey]);

  const search = useQuery({
    queryKey: [
      "dms",
      "search",
      selectedCategory ?? "all",
      browseQuery.trim(),
      browseSortKey,
      browsePage,
      showArchived ? "incl-archived" : "active",
    ],
    enabled: !selectedRootId,
    queryFn: () =>
      foldersApi.searchFolders({
        category: selectedCategory ?? undefined,
        q: browseQuery.trim() || undefined,
        sort: browseSortBy,
        dir: browseSortDir,
        page: browsePage,
        pageSize: ROOT_BROWSER_PAGE_SIZE,
        includeArchived: showArchived,
      }),
    placeholderData: (prev) => prev,
  });
  const categories = search.data?.categories ?? [];

  // Folder Rename & Archive v1.0 — inline rename state. Only one folder
  // is in rename mode at a time; submitting or cancelling clears it.
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);

  // Folder Visibility & Access Control v1.0 — folder whose Permissions modal
  // is open (ADMIN only; the kebab item is gated server-side via canManageAccess).
  const [permissionsTarget, setPermissionsTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const invalidateTrees = () => {
    qc.invalidateQueries({ queryKey: ["dms", "rootTree"] });
    qc.invalidateQueries({ queryKey: ["dms", "search"] });
    qc.invalidateQueries({ queryKey: ["dms", "folderDetail", selectedFolderId] });
    qc.invalidateQueries({ queryKey: ["folder-picker-tree"] });
  };

  const renameFolder = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      foldersApi.rename(id, name),
    onSuccess: (folder) => {
      setRenamingFolderId(null);
      setRenameValue("");
      setRenameError(null);
      invalidateTrees();
      toast({ message: `Renamed to "${folder.name}".`, tone: "success" });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : "Could not rename folder.";
      setRenameError(msg);
    },
  });

  const archiveFolder = useMutation({
    mutationFn: (id: string) => foldersApi.archive(id),
    onSuccess: (folder) => {
      invalidateTrees();
      toast({
        message: `Archived "${folder.name}". Toggle 'Show archived' to find it again.`,
        tone: "success",
        action:
          canViewArchived && !showArchived
            ? { label: "Show archived", onClick: () => setShowArchived(true) }
            : undefined,
      });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : "Could not archive folder.";
      toast({ message: msg, tone: "error" });
    },
  });

  const unarchiveFolder = useMutation({
    mutationFn: (id: string) => foldersApi.unarchive(id),
    onSuccess: (folder) => {
      invalidateTrees();
      toast({ message: `Restored "${folder.name}".`, tone: "success" });
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : "Could not restore folder.";
      toast({ message: msg, tone: "error" });
    },
  });

  const startRename = ({ folder }: FolderTreeAction) => {
    setRenamingFolderId(folder.id);
    setRenameValue(folder.name);
    setRenameError(null);
  };
  const cancelRename = () => {
    setRenamingFolderId(null);
    setRenameValue("");
    setRenameError(null);
  };
  const submitRename = () => {
    if (!renamingFolderId) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameError("Folder name is required.");
      return;
    }
    if (trimmed.length > 200) {
      setRenameError("Folder name cannot exceed 200 characters.");
      return;
    }
    renameFolder.mutate({ id: renamingFolderId, name: trimmed });
  };

  // Cancel rename if the folder being renamed disappears (e.g. archived
  // and Show archived is off, the user navigated away, or the pinned
  // root tree changed).
  useEffect(() => {
    if (!renamingFolderId) return;
    if (trees.length === 0) return;
    const found = trees.some((t) => containsFolder(t, renamingFolderId));
    if (!found) cancelRename();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trees, renamingFolderId]);

  const breadcrumb = useQuery({
    queryKey: ["dms", "breadcrumb", selectedFolderId],
    enabled: !!selectedFolderId,
    queryFn: () => foldersApi.breadcrumb(selectedFolderId!),
  });

  // When a root is pinned but no inner folder has been picked yet,
  // select the root itself so the right pane has something to show.
  useEffect(() => {
    if (!selectedFolderId && selectedRootId && tree.data) {
      setSelectedFolderId(tree.data.id);
    }
  }, [tree.data, selectedRootId, selectedFolderId]);

  const selectedFolderName = useMemo(
    () => findNodeName(trees, selectedFolderId),
    [trees, selectedFolderId],
  );

  const createFolder = useMutation({
    mutationFn: (name: string) =>
      foldersApi.create({ name, parentFolderId: selectedFolderId ?? null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dms", "rootTree"] });
      qc.invalidateQueries({ queryKey: ["dms", "folderDetail", selectedFolderId] });
      qc.invalidateQueries({ queryKey: ["folder-picker-tree"] });
    },
  });

  return (
    <>
      <PageHeader
        title="Documents"
        description="Unified file system for loans, brokers, vendors, portfolios, and company records."
      />

      <div className="flex items-center gap-space-2 mb-space-3 flex-wrap">
        {(breadcrumb.data ?? []).map((seg, i, arr) => (
          <span key={seg.id} className="flex items-center gap-space-1 text-small text-gray-mid">
            <button
              type="button"
              className="hover:underline"
              onClick={() => setSelectedFolderId(seg.id)}
            >
              {seg.name}
            </button>
            {i < arr.length - 1 && <ChevronRight size={12} aria-hidden />}
          </span>
        ))}
      </div>

      {/* Layout redesign — top bar replaces the old left categories
          sidebar. Category pills row, then a controls row (search, sort,
          filter, archived toggle) with New folder + Upload on the right. */}
      <div className="flex flex-col gap-space-3 mb-space-3">
        {categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-space-1">
            {categories.map((c) => {
              const active = selectedCategory === c.category;
              return (
                <button
                  key={c.category}
                  type="button"
                  onClick={() => setSelectedCategory(active ? null : c.category)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-space-1 px-space-2 h-[26px] rounded-full text-caption border transition-colors ${
                    active
                      ? "bg-onyx text-white-pure border-onyx"
                      : "bg-white-pure text-onyx border-border-light hover:bg-warm-white"
                  }`}
                >
                  <span>{c.category}</span>
                  <span
                    className={`inline-flex items-center justify-center min-w-[20px] h-[16px] px-[5px] rounded-full text-[10px] font-display font-semibold ${
                      active ? "bg-white-pure/20 text-white-pure" : "bg-warm-white text-gray-mid"
                    }`}
                  >
                    {c.count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center flex-wrap gap-space-2">
          <div className="relative w-[240px] max-w-full">
            <Search
              size={14}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              aria-label="Search folders"
              className="pl-[28px]"
              placeholder={
                selectedCategory
                  ? `Search ${selectedCategory.toLowerCase()} folders…`
                  : "Search folders…"
              }
              value={browseQuery}
              onChange={(e) => {
                setBrowseQuery(e.target.value);
                setBrowsePage(1);
              }}
            />
          </div>

          <select
            aria-label="Sort folders"
            className="h-8 px-space-2 rounded-md border border-border-light text-small bg-white-pure"
            value={browseSortKey}
            onChange={(e) => {
              setBrowseSortKey(e.target.value);
              setBrowsePage(1);
            }}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={`${opt.value}|${opt.dir}`} value={`${opt.value}|${opt.dir}`}>
                {opt.label}
              </option>
            ))}
          </select>

          <div className="relative">
            <Button
              ref={filterAnchorRef}
              variant="secondary"
              size="sm"
              onClick={() => setFilterOpen((v) => !v)}
              aria-expanded={filterOpen}
              aria-haspopup="dialog"
            >
              <Filter size={14} aria-hidden /> Filter
              {activeFilterCount > 0 && (
                <span
                  className="ml-space-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-full bg-gold text-onyx text-caption font-display font-semibold"
                  aria-label={`${activeFilterCount} active filters`}
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>
            <DocumentFilterPanel
              open={filterOpen}
              anchorRef={filterAnchorRef}
              onClose={() => setFilterOpen(false)}
              initial={filters}
              onApply={applyFilters}
              onClear={clearFilters}
            />
          </div>

          {canViewArchived && (
            <ShowArchivedToggle checked={showArchived} onChange={setShowArchived} />
          )}

          <div className="ml-auto flex items-center gap-space-2">
            {canCreateFolder && (
              <Button
                variant="secondary"
                size="sm"
                disabled={!selectedFolderId}
                onClick={async () => {
                  const name = await dialogs.prompt({
                    title: "New folder",
                    label: "Folder name",
                    placeholder: "e.g. Compliance Q1",
                  });
                  if (name && name.trim()) createFolder.mutate(name.trim());
                }}
              >
                <FolderPlus size={14} aria-hidden /> New folder
              </Button>
            )}
            {canUpload && (
              <Button
                size="sm"
                disabled={!selectedFolderId}
                onClick={() => setUploadOpen(true)}
              >
                <Upload size={14} aria-hidden /> Upload
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-space-4">
        <aside
          className={`shrink-0 transition-all duration-200 ${
            treeCollapsed ? "w-[56px]" : "w-[280px]"
          }`}
        >
          <div className="flex items-center gap-space-1 mb-space-2">
            <button
              type="button"
              onClick={() => setTreeCollapsed((v) => !v)}
              aria-label={treeCollapsed ? "Expand folder panel" : "Collapse folder panel"}
              aria-expanded={!treeCollapsed}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-mid hover:bg-warm-white hover:text-onyx"
            >
              {treeCollapsed ? (
                <PanelLeftOpen size={16} aria-hidden />
              ) : (
                <PanelLeftClose size={16} aria-hidden />
              )}
            </button>
            {!treeCollapsed && selectedRootId && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedRoot(null)}
              >
                <ArrowLeft size={14} aria-hidden /> Back to browse
              </Button>
            )}
          </div>

          {!treeCollapsed &&
            (selectedRootId ? (
              trees.length > 0 ? (
                <FolderTree
                  nodes={trees}
                  selectedId={selectedFolderId}
                  onSelect={(n) => {
                    if (renamingFolderId && renamingFolderId !== n.id) {
                      cancelRename();
                    }
                    setSelectedFolderId(n.id);
                  }}
                  initiallyExpanded
                  onRenameRequested={startRename}
                  onArchiveRequested={({ folderId }) =>
                    archiveFolder.mutate(folderId)
                  }
                  onUnarchiveRequested={({ folderId }) =>
                    unarchiveFolder.mutate(folderId)
                  }
                  onPermissionsRequested={({ folder }) =>
                    setPermissionsTarget({ id: folder.id, name: folder.name })
                  }
                  renamingFolderId={renamingFolderId}
                  renderRenameInput={(folder) => (
                    <FolderRenameInput
                      initialName={folder.name}
                      value={renameValue}
                      error={renameError}
                      busy={renameFolder.isPending}
                      onChange={(v) => {
                        setRenameValue(v);
                        if (renameError) setRenameError(null);
                      }}
                      onSubmit={submitRename}
                      onCancel={cancelRename}
                    />
                  )}
                />
              ) : (
                <p className="text-caption text-gray-mid">
                  {tree.isLoading ? "Loading folder tree…" : "No folders here yet."}
                </p>
              )
            ) : (
              <EntityRootBrowser
                category={selectedCategory}
                q={browseQuery}
                hits={search.data?.hits}
                isError={search.isError}
                isFetching={search.isFetching}
                page={browsePage}
                onPageChange={setBrowsePage}
                onRootSelect={(root) => setSelectedRoot(root)}
              />
            ))}
        </aside>

        <section className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-h3 text-onyx m-0 mb-space-3">
            {selectedFolderName ?? "Select a folder"}
          </h2>

          {selectedFolderId && (
            <FolderContents
              folderId={selectedFolderId}
              onFolderSelect={(id) => setSelectedFolderId(id)}
              archiveScope={showArchived ? "All" : "ActiveOnly"}
              onShowArchivedRequested={
                canViewArchived && !showArchived
                  ? () => setShowArchived(true)
                  : undefined
              }
              filters={filters}
            />
          )}
        </section>
      </div>

      <FolderPermissionsModal
        open={permissionsTarget !== null}
        onClose={() => setPermissionsTarget(null)}
        folderId={permissionsTarget?.id ?? null}
        folderName={permissionsTarget?.name}
        onSaved={invalidateTrees}
      />

      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        targetFolderId={selectedFolderId}
        targetFolderName={selectedFolderName ?? undefined}
        onUploaded={() => {
          qc.invalidateQueries({ queryKey: ["dms", "folderDetail", selectedFolderId] });
          qc.invalidateQueries({ queryKey: ["dms", "folderDocs", selectedFolderId] });
          qc.invalidateQueries({ queryKey: ["dms", "rootTree"] });
        }}
      />
    </>
  );
}

function ShowArchivedToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-space-2 text-small text-gray-mid hover:text-onyx"
    >
      <span
        className={`relative inline-block w-[32px] h-[18px] rounded-full transition-colors ${
          checked ? "bg-gold" : "bg-border-light"
        }`}
        aria-hidden
      >
        <span
          className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white-pure shadow transition-transform ${
            checked ? "translate-x-[14px]" : ""
          }`}
        />
      </span>
      Show archived
    </button>
  );
}

function findNodeName(
  trees: DocumentFolderTreeDto[],
  id: string | null,
): string | null {
  if (!id) return null;
  for (const t of trees) {
    const found = findNode(t, id);
    if (found) return found.name;
  }
  return null;
}

function findNode(
  node: DocumentFolderTreeDto,
  id: string,
): DocumentFolderTreeDto | null {
  if (node.id === id) return node;
  for (const c of node.children) {
    const m = findNode(c, id);
    if (m) return m;
  }
  return null;
}

function containsFolder(node: DocumentFolderTreeDto, id: string): boolean {
  if (node.id === id) return true;
  for (const c of node.children) if (containsFolder(c, id)) return true;
  return false;
}

interface FolderRenameInputProps {
  initialName: string;
  value: string;
  error: string | null;
  busy: boolean;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * Folder Rename & Archive v1.0 — inline edit input rendered in place of
 * the folder name when the user picks Rename from the kebab menu. Enter
 * submits, Escape cancels. Validation errors render below in red.
 */
function FolderRenameInput({
  initialName,
  value,
  error,
  busy,
  onChange,
  onSubmit,
  onCancel,
}: FolderRenameInputProps) {
  return (
    <span
      className="flex items-center gap-space-1"
      onClick={(e) => e.stopPropagation()}
    >
      <Input
        autoFocus
        value={value}
        aria-label={`Rename ${initialName}`}
        aria-invalid={!!error}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSubmit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
        className={`h-7 text-small ${
          error ? "border-status-error focus-visible:shadow-[0_0_0_3px_rgba(220,38,38,0.25)]" : ""
        }`}
      />
      <button
        type="button"
        aria-label="Save folder name"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onSubmit();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-status-success hover:bg-status-success/10 disabled:opacity-50"
      >
        <Check size={14} strokeWidth={1.5} aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Cancel rename"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-mid hover:bg-warm-white disabled:opacity-50"
      >
        <X size={14} strokeWidth={1.5} aria-hidden />
      </button>
      {error && (
        <span className="ml-space-1 text-caption text-status-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
